/**
 * The 👍/👎 verdict on an assistant reply, as data.
 *
 * The reason codes and the comment ceiling mirror the backend's
 * `FeedbackReasonCode` and `COMMENT_MAX`; the generated types still check the
 * request body at the call site. Pure and import-free, so `node --test` can
 * import it directly.
 */

export type FeedbackRatingValue = "HELPFUL" | "NOT_HELPFUL";

export type FeedbackReasonValue =
  | "WRONG_FORM"
  | "TOO_MANY_QUESTIONS"
  | "MISUNDERSTOOD"
  | "INCORRECT"
  | "OTHER";

/** Quick reasons offered with a thumbs-down, in display order. "Other" last. */
export const FEEDBACK_REASON_CODES: readonly FeedbackReasonValue[] = [
  "WRONG_FORM",
  "TOO_MANY_QUESTIONS",
  "MISUNDERSTOOD",
  "INCORRECT",
  "OTHER",
];

/** Matches the backend's `COMMENT_MAX`. */
export const FEEDBACK_COMMENT_MAX = 1000;

export interface FeedbackDraft {
  messageId: string;
  rating: FeedbackRatingValue;
  reasons?: readonly string[];
  comment?: string | null;
}

export interface FeedbackRequestBody {
  target_type: "ASSISTANT_MESSAGE";
  target_id: string;
  rating: FeedbackRatingValue;
  reason_codes: FeedbackReasonValue[];
  comment: string | null;
}

/** Keep only known reason codes, once each, in the order they were chosen. */
export function normaliseReasons(values: readonly string[]): FeedbackReasonValue[] {
  const known = new Set<string>(FEEDBACK_REASON_CODES);
  const seen = new Set<string>();
  const out: FeedbackReasonValue[] = [];
  for (const value of values) {
    if (known.has(value) && !seen.has(value)) {
      seen.add(value);
      out.push(value as FeedbackReasonValue);
    }
  }
  return out;
}

/**
 * Build the `submitFeedback` body from what the reader chose.
 *
 * A thumbs-up carries no reasons and no comment — they explain a miss, and the
 * server clears them anyway. The comment is trimmed, capped, and sent as null
 * when empty.
 */
export function buildFeedbackBody(draft: FeedbackDraft): FeedbackRequestBody {
  const helpful = draft.rating === "HELPFUL";
  const trimmed = (draft.comment ?? "").trim().slice(0, FEEDBACK_COMMENT_MAX);
  return {
    target_type: "ASSISTANT_MESSAGE",
    target_id: draft.messageId,
    rating: draft.rating,
    reason_codes: helpful ? [] : normaliseReasons(draft.reasons ?? []),
    comment: helpful || trimmed === "" ? null : trimmed,
  };
}

/** Share of thumbs-up as a whole percentage, or null when there is nothing to divide. */
export function helpfulPercent(helpful: number, total: number): number | null {
  if (total <= 0) {
    return null;
  }
  return Math.round((helpful / total) * 100);
}
